import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPlatformBranding, PLATFORM_LOGO_URL } from '../api/platformBranding';

const SIZES = {
  sm: 'h-9 w-9 text-sm',
  md: 'h-11 w-11 text-lg',
  lg: 'h-16 w-16 text-2xl',
  // §19: sidebar header logo, stacked above "ATLAS PORTAL" rather than
  // beside it - larger since it's no longer competing with the wordmark
  // for horizontal space.
  xl: 'h-24 w-24 text-4xl',
} as const;

// Oakford Technology's own brand mark. Driven by the platform-branding
// query (same ['platform-branding'] cache key the admin upload page
// invalidates on a successful upload), NOT by a bare <img onError> latch -
// the latter gets stuck showing the fallback forever once it fails once
// (which it always will, on first load before any logo has been uploaded),
// even after an admin uploads a logo later in the same session, because
// nothing would ever tell a plain onError-driven component to retry.
export function BrandMark({ size = 'md' }: { size?: keyof typeof SIZES }) {
  const { data } = useQuery({
    queryKey: ['platform-branding'],
    queryFn: getPlatformBranding,
    staleTime: 60_000,
  });
  const [imgFailed, setImgFailed] = useState(false);
  const dims = SIZES[size];

  // A fresh upload gets a new updatedAt - retry rendering it even if a
  // previous (different) file had failed to load.
  useEffect(() => {
    setImgFailed(false);
  }, [data?.updatedAt]);

  if (data?.hasLogo && !imgFailed) {
    return (
      <img
        src={`${PLATFORM_LOGO_URL}?v=${encodeURIComponent(data.updatedAt)}`}
        alt="Oakford Technology"
        className={`${dims} shrink-0 rounded-lg object-contain shadow-sm`}
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <div
      className={`flex ${dims} shrink-0 items-center justify-center rounded-lg bg-brand-700 font-bold text-white shadow-sm`}
    >
      O
    </div>
  );
}
